<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { usePvPageShell } from "@modules/pv/composables/usePvPageShell"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            noPdl: "Il n’y a pas de compteur électrique (PDL) sur ce bâtiment pour afficher les données PV.",
            errorLoad:
                "Impossible de charger les données PV pour cette source.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const pvStore = usePvStore()
const shell = usePvPageShell()
const buildingStore = useBuildingStore()

const {
    sourcePvData,
    sourceId,
    isFetchingSourcePvData,
    hasErrorSourcePvData,
    errorSourcePvData,
} = storeToRefs(pvStore)

const { isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

// ================================
// COMPUTED
// ================================
const getIsBuildingOrSourcesLoading = computed(
    () => isFetchingBuilding.value || isFetchingBuildingSources.value
)

/** Skeleton : bâtiment + sources (shell bâtiment) ou données PV source. */
const getIsConsommationContentLoading = computed(
    () => getIsBuildingOrSourcesLoading.value || isFetchingSourcePvData.value
)

const getVariables = computed(
    () => sourcePvData.value?.variables as Record<string, unknown> | undefined
)

const getConsoChart = computed(() => getVariables.value?.energie_elec_tout)

const getExtrapolationDaily = computed(
    () => (getVariables.value?.extrapolation_pv_daily as unknown) ?? null
)

const getNbHourlyDays = computed(() => {
    const v = getVariables.value?.nb_hourly_days
    return typeof v === "number" ? v : 0
})

const getExtrapolationAnalyseId = computed(() => pvStore.getExtrapolationId)

const getBuildingId = computed(() => shell.getBuildingId.value ?? "")

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Chargement initial `GET …/data/source/:id/pv` : déclenché par
 * {@link HeaderPvBuilding} (toutes les sous-routes PV) pour `sourcePvData` et
 * `lastExtrapolationAnalyseId`. Ici : rafraîchissement manuel depuis la carte.
 */
async function onRefreshedPv(): Promise<void> {
    const sid = sourceId.value
    if (!sid) return
    pvStore.unsetExtrapolation()
    await pvStore.fetchSourcePvData({ sourceId: sid })
}
</script>

<template>
    <div class="page page-building-pv-consommation p-0">
        <!-- ================================ -->
        <!-- Chargement (bâtiment / sources / données PV source) -->
        <!-- ================================ -->
        <div v-if="getIsConsommationContentLoading" class="flex flex-col gap-3">
            <BaseSkeleton height="3rem" class="w-full rounded-md" />
            <BaseSkeleton height="28rem" class="w-full rounded-md" />
        </div>

        <!-- ================================ -->
        <!-- États sans données ou erreurs -->
        <!-- ================================ -->
        <BaseMessage
            v-else-if="!pvStore.getSourceId"
            severity="info"
            :closable="false"
        >
            {{ t("noPdl") }}
        </BaseMessage>

        <BaseMessage
            v-else-if="hasErrorSourcePvData"
            severity="error"
            :closable="false"
        >
            {{ errorSourcePvData?.message || t("errorLoad") }}
        </BaseMessage>

        <!-- ================================ -->
        <!-- Carte principale (équivalent legacy PVConso) -->
        <!-- ================================ -->
        <PvConsommationElectriciteCard
            v-else-if="getConsoChart && getBuildingId && sourceId"
            :conso-data="getConsoChart"
            :extrapolation-data="getExtrapolationDaily"
            :nb-hourly-days="getNbHourlyDays"
            :id-extrapolation="getExtrapolationAnalyseId"
            :building-id="getBuildingId"
            :source-id="sourceId"
            @refreshed="onRefreshedPv"
        />

        <BaseMessage v-else-if="sourcePvData" severity="warn" :closable="false">
            {{ t("errorLoad") }}
        </BaseMessage>
    </div>
</template>
